<template>
  <div class="pb-16">
    <NavHeader
      :title="$t('screens.foodCourtOrder.title')"
      @onBackClick="goBack"
    />
    <div v-if="isInitialLoad" class="d-flex justify-center pt-8">
      <v-progress-circular indeterminate color="black" />
    </div>
    <div v-else>
      <LayoutCard
        v-if="sessionPhoneNumber && smsNotificationsEnabled"
        rounded
        :title="$t('screens.order.smsDisclaimerTitle')"
      >
        <div class="tp-text-body text-left">
          {{
            $t('screens.order.smsDisclaimer', {
              phoneNumber: sessionPhoneNumber,
            })
          }}
          <Button gray x-small dark @click="editPhoneNumber">
            {{ $t('screens.order.smsCta') }}
          </Button>
        </div>
      </LayoutCard>
      <NotificationBanner
        :is-open="!sessionPhoneNumber && smsNotificationsEnabled"
        @cta-click="showAllowNotificationsModal = true"
      />
      <div
        v-for="order in ordersData"
        :key="`order-${order.order.unique_order_number}`"
        class="mb-4"
      >
        <FoodCourtMerchantOrderStatus
          :unique-order-number="order.order.unique_order_number"
          :show-products-by-default="showProductsByDefault"
          :runner-service-enabled="runnerServiceEnabled"
        />
      </div>
      <LayoutCard class="mb-4" rounded>
        <PaymentInformation
          show-subtotal
          show-payment-type
          :show-transaction-fee="!!paymentData.transaction_fee"
          :show-tips="!!paymentData.tips"
          :transaction-fee="paymentData.transaction_fee"
          :tips="paymentData.tips"
          :total="paymentData.total"
          :subtotal="paymentData.subtotal"
          :payments-sum="paymentData.payments_sum"
          :payment-data="paymentData.payment_data"
          :discount-code="paymentData.coupon"
          :discount-amount="paymentData.discount"
          :pay-at-restaurant="paymentData.pay_at_restaurant"
          :small-order-fee="paymentData.small_order_fee"
        />
        <RequestReceiptButton v-if="paymentUuid" :payment-uuid="paymentUuid" />
        <div v-if="paymentUuid && invoicesEnabled" class="mt-6">
          <Button gray x-small dark @click="goToRequestInvoice">
            {{ $t('components.requestInvoice.button') }}
          </Button>
        </div>
      </LayoutCard>
      <LayoutCard rounded>
        <!-- Only the first one since we do not really care about the other ones -->
        <ReviewGreet
          :unique-order-number="ordersData[0]?.order.unique_order_number"
          :session="ordersData[0]?.order.session_token"
        />
      </LayoutCard>
    </div>
    <NotificationModal
      :is-open="showAllowNotificationsModal"
      @close-click="closeNotificationsModal"
      @cta-click="savePhoneNumber"
    />
  </div>
</template>

<script>
import NavHeader from '@/components/Navigation/NavHeader.vue';
import LayoutCard from '../../components/LayoutCard.vue';
import PaymentInformation from '@/components/Orders/OrderInfo/PaymentInformation.vue';
import FoodCourtMerchantOrderStatus from '@/views/FoodCourt/FoodCourtMerchantOrderStatus.vue';
import NotificationBanner from '@/components/Orders/NotificationBanner.vue';
import NotificationModal from '@/components/Orders/NotificationModal.vue';
import RequestReceiptButton from '@/components/Payments/RequestReceiptButton.vue';
import Button from '@/components/Button.vue';
import ReviewGreet from '@/components/Reviews/ReviewGreet.vue';
import { delay } from 'lodash';

export default {
  name: 'FoodCourtViewMultiMerchantOrder',
  components: {
    NavHeader,
    LayoutCard,
    PaymentInformation,
    FoodCourtMerchantOrderStatus,
    NotificationBanner,
    NotificationModal,
    RequestReceiptButton,
    Button,
    ReviewGreet,
  },
  data() {
    return {
      isInitialLoad: false,
      showAllowNotificationsModal: false,
      paymentData: {
        is_paid: false,
        subtotal: 0,
        total: 0,
        transaction_fee: 0,
        tips: 0,
        payments_sum: 0,
        payment_data: {},
        coupon: null,
        discount: 0,
        pay_at_restaurant: false,
        small_order_fee: 0,
      },
      ordersData: [],
    };
  },
  computed: {
    foodCourtHash() {
      return this.$route.params.foodCourtHash;
    },
    paymentUuid() {
      return this.$route.params.paymentUuid || this.$route.query.paymentUuid;
    },
    placedOrdersIds() {
      return this.$route.params.placedOrdersIds
        ? this.$route.params.placedOrdersIds.split(',')
        : [];
    },
    foodCourtSession() {
      return this.$store.getters.getFoodCourtSession;
    },
    sessionPhoneNumber() {
      return this.$store.getters.getFoodCourtUserPhoneNumber;
    },
    orderSessions() {
      return this.ordersData.map(order => order.order.session_token);
    },
    showProductsByDefault() {
      return this.orderSessions.length <= 1;
    },
    invoicesEnabled() {
      return this.$store.getters.getIsFoodCourtSettingEnabled('invoices');
    },
    smsNotificationsEnabled() {
      return this.$store.getters.getIsFoodCourtSettingEnabled(
        'sms-notifications'
      );
    },
    runnerServiceEnabled() {
      return this.$store.getters.getIsFoodCourtSettingEnabled('runner-service');
    },
  },
  async mounted() {
    this.isInitialLoad = true;

    window.scrollTo({ top: 0 });

    const data = await this.$store.dispatch('fetchMultiMerchantOrderDetails', {
      paymentUuid: this.paymentUuid,
    });

    if (data) {
      this.paymentData = {
        is_paid: data.is_paid,
        subtotal: data.amount,
        total: data.total,
        transaction_fee: data.transaction_fee,
        tips: data.tips,
        payments_sum: data.payments_sum,
        payment_data: data.payment_data,
        coupon: data.coupon,
        discount: data.discount,
        pay_at_restaurant: data.pay_at_restaurant,
        small_order_fee: data.small_order_fee,
      };

      this.ordersData = data.orders || [];
    }

    if (!this.sessionPhoneNumber && this.smsNotificationsEnabled) {
      delay(() => (this.showAllowNotificationsModal = true), 2000);
    }

    // if there is a session phone number, save it in the store
    if (this.sessionPhoneNumber) {
      await this.saveSessionPhoneNumberToOrders();
    }

    this.isInitialLoad = false;
  },
  methods: {
    goBackToFoodCourt() {
      return this.$router.push({
        name: 'FoodCourt',
        params: { foodCourtHash: this.foodCourtHash },
        replace: true,
      });
    },
    goBack() {
      const origin = this.$route.query.origin;
      if (origin === 'foodcourt') {
        this.goBackToFoodCourt();
        return;
      }

      this.$router.go(-1);
    },
    async savePhoneNumber(countryCallingCode, nationalNumber) {
      if (countryCallingCode && nationalNumber) {
        await this.$store.dispatch('assignPhoneNumberToRestaurantSessions', {
          countryCallingCode,
          nationalNumber,
          sessions: this.orderSessions,
        });
      }
      this.showAllowNotificationsModal = false;
      this.showAllowNotificationsBanner = false;
    },
    async saveSessionPhoneNumberToOrders() {
      const phoneParts = this.$store.getters.getFoodCourtUserPhoneNumberParts;

      if (phoneParts.countryCallingCode && phoneParts.nationalNumber) {
        try {
          await this.$store.dispatch('assignPhoneNumberToRestaurantSessions', {
            countryCallingCode: phoneParts.countryCallingCode,
            nationalNumber: phoneParts.nationalNumber,
            sessions: this.orderSessions,
          });
        } catch (error) {
          console.error('Error saving session phone number to orders:', error);
        }
      }
    },
    closeNotificationsModal() {
      this.showAllowNotificationsModal = false;
      this.showAllowNotificationsBanner = true;
      this.$store.dispatch('sendAnalyticsEvent', {
        event: 'disallow_notifications',
      });
    },
    editPhoneNumber() {
      this.showAllowNotificationsModal = true;
      this.showAllowNotificationsBanner = false;
    },
    goToRequestInvoice() {
      this.$router.push({
        name: 'RequestInvoice',
        params: {
          paymentUuid: this.paymentUuid,
        },
      });
    },
  },
};
</script>
